import { XhrError } from './error';

const JSON_RESPONSE = 'json';
const TEXT_RESPONSE = 'text';

export class RequestError extends Error {
  constructor(kind, message) {
    super(message);

    this.name = 'RequestError';
    this.kind = kind;
  }
}

function parse(req, responseType) {
  if (req.statusText === 204 || req.statusText === 205) {
    return null;
  }

  switch (responseType) {
    case JSON_RESPONSE:
      return JSON.parse(req.responseText);
    case TEXT_RESPONSE:
      return req.responseText;
    default:
      return req.response;
  }
}

export function request(url, options = {}, responseType) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;

    const finish = (callback, value) => {
      if (settled) return;

      settled = true;
      options.signal?.removeEventListener('abort', abort);
      callback(value);
    };

    const abort = () => {
      xhr.abort();
      finish(reject, new RequestError('abort', 'Request was aborted'));
    };

    if (options.signal?.aborted) {
      finish(reject, new RequestError('abort', 'Request was aborted'));

      return;
    }

    if (typeof options.progress === 'function') {
      xhr.upload.onprogress = options.progress;
    }

    xhr.open(options.method || 'GET', url, true);
    if (options.timeout !== undefined) xhr.timeout = options.timeout;

    if (options.headers) {
      Object.keys(options.headers).forEach((key) => {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (options.rawResponse) {
          finish(resolve, xhr);
        } else {
          finish(resolve, parse(xhr, responseType));
        }
      } else {
        const error = new XhrError(xhr);
        error.kind = 'http';

        finish(reject, error);
      }
    };

    xhr.onerror = () => {
      const error = new XhrError(xhr);
      error.kind = 'network';

      finish(reject, error);
    };

    xhr.ontimeout = () => finish(reject, new RequestError('timeout', 'Request timed out'));
    xhr.onabort = () => finish(reject, new RequestError('abort', 'Request was aborted'));

    xhr.onloadend = () => {
      if (xhr.status === 0) {
        finish(reject, new RequestError('network', 'Network failure'));
      }
    };

    options.signal?.addEventListener('abort', abort, { once: true });

    try {
      xhr.send(options.body);
    } catch (error) {
      finish(reject, error);
    }
  });
}

export function requestJson(url, options) {
  return request(url, options, JSON_RESPONSE);
}

export function requestText(url, options) {
  return request(url, options, TEXT_RESPONSE);
}
